import React, { useState, useEffect, useRef } from "react";

const Ecosystem = () => {
  const [hasAnimated, setHasAnimated] = useState(false);
  const [counts, setCounts] = useState({ count1: 0, count2: 0, count3: 0 });
  const sectionRef = useRef(null);

  const ecosystemData = [
    {
      id: 1,
      targetNumber: 380,
      label: "Empresas",
      description:
        "Forma parte de una comunidad de empresas e industrias más grande de México.",
      countKey: "count1",
    },
    {
      id: 2,
      targetNumber: 47500,
      label: "Citas anuales programadas",
      description:
        "Crea sinergias con organismos gubernamentales y privados para generar impacto industrial.",
      countKey: "count2",
    },
    {
      id: 3,
      targetNumber: 20,
      label: "Eventos Anuales de relacionamiento",
      description:
        "Optimiza tu red de contactos y expande tu negocio con compradores de grandes Industrias.",
      countKey: "count3",
    },
  ];

  useEffect(() => {
    // Respetar preferencia de movimiento reducido
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);

          // Si el usuario prefiere menos movimiento, mostrar valores finales
          if (prefersReducedMotion) {
            setCounts({
              count1: ecosystemData[0].targetNumber,
              count2: ecosystemData[1].targetNumber,
              count3: ecosystemData[2].targetNumber,
            });
            return;
          }

          const duration = 2000;
          let startTime = null;
          let rafId;

          // Easing: desacelera al final para un efecto más natural
          const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

          const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const elapsed = timestamp - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easeOutQuart(progress);

            setCounts({
              count1: Math.floor(
                ecosystemData[0].targetNumber * easedProgress,
              ),
              count2: Math.floor(
                ecosystemData[1].targetNumber * easedProgress,
              ),
              count3: Math.floor(
                ecosystemData[2].targetNumber * easedProgress,
              ),
            });

            if (progress < 1) {
              rafId = requestAnimationFrame(animate);
            } else {
              // Asegurar valores finales exactos
              setCounts({
                count1: ecosystemData[0].targetNumber,
                count2: ecosystemData[1].targetNumber,
                count3: ecosystemData[2].targetNumber,
              });
            }
          };

          rafId = requestAnimationFrame(animate);

          // Cleanup si el componente se desmonta durante la animación
          return () => {
            if (rafId) cancelAnimationFrame(rafId);
          };
        }
      },
      { threshold: 0.2 },
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasAnimated]);

  return (
    <section
      ref={sectionRef}
      className="py-10 sm:py-12 md:py-16 px-4 sm:px-6 bg-gradient-to-b from-white to-gray-50"
    >
      <div className="max-w-7xl mx-auto">
        {/* Título */}
        <div className="text-center mb-8 sm:mb-10 md:mb-12">
          <h2 className="text-[#1e72b9] text-lg sm:text-xl font-semibold tracking-wider uppercase mb-2">
            ECOSISTEMA EMPRESARIAL
          </h2>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#29265b] mb-4 sm:mb-6">
            Nuestro Ecosistema
          </h2>
          <p className="text-gray-700 text-base sm:text-lg md:text-xl max-w-4xl mx-auto leading-relaxed px-2">
            Únete a la red empresarial que conecta, impulsa y expande negocios.
            Colabora con empresas líderes, accede a oportunidades estratégicas y
            amplía tu impacto en la industria.
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 mt-8 sm:mt-10 md:mt-12">
          {ecosystemData.map((item) => (
            <div
              key={item.id}
              className="bg-[#2a3180] rounded-[40px] sm:rounded-[50px] md:rounded-[60px] p-6 sm:p-8 text-center transform transition-all duration-300 hover:scale-105 hover:shadow-2xl"
            >
              <p className="text-white text-sm sm:text-base md:text-lg mb-4 sm:mb-6 leading-relaxed min-h-[60px] sm:min-h-[80px] flex items-center justify-center">
                {item.description}
              </p>
              <div className="mt-4 sm:mt-6">
                <h3 className="text-[#39a8e0] text-4xl sm:text-5xl md:text-6xl font-bold mb-2">
                  + {counts[item.countKey].toLocaleString()}
                </h3>
                <p className="text-[#39a8e0] text-lg sm:text-xl md:text-2xl font-semibold">
                  {item.label}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Ecosystem;
